import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Newspaper, 
  Users, 
  Flag, 
  Handshake, 
  Image, 
  Sliders, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Plus, 
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Calendar,
  TrendingUp,
  Mail,
  MessageSquare,
  ShipWheel
} from 'lucide-react';
import AdminGallery from './AdminGallery';
import AdminNews from './AdminNews';
import AdminAthletes from './AdminAthletes';
import AdminFederations from './AdminFederations';
import AdminPartners from './AdminPartners';
import AdminSliders from './AdminSliders';
import AdminMessages from './AdminMessages';
import AdminSettings from './AdminSettings';
import AdminTour from './AdminTour';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState({
    news: 0,
    athletes: 0,
    federations: 0,
    partners: 0,
    galleries: 0,
    sliders: 0,
    users: 0,
    messages: 0
  });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  // Sample data for different sections
  const [newsData, setNewsData] = useState([
    {
      id: 1,
      title: 'Africa Surf Tour 2025 : Les Dates Révélées',
      category: 'competitions',
      author: 'Équipe ASCsurf',
      status: 'published',
      featured: true,
      created_at: '2024-12-15T10:00:00Z',
      views: 1250
    },
    {
      id: 2,
      title: 'Cherif Fall Qualifié pour les World Surf Games',
      category: 'athletes',
      author: 'Mohamed Kone',
      status: 'published',
      featured: false,
      created_at: '2024-12-10T14:30:00Z',
      views: 890
    },
    {
      id: 3,
      title: 'Nouveau Centre de Formation à Taghazout',
      category: 'development',
      author: 'Sarah Benali',
      status: 'draft',
      featured: false,
      created_at: '2024-12-05T09:00:00Z',
      views: 0
    }
  ]);

  const [athletesData, setAthletesData] = useState([
    {
      id: 1,
      name: 'Cherif Fall',
      country: 'Sénégal',
      category: 'professional',
      ranking: 1,
      competitions: 28,
      wins: 12,
      status: 'active'
    },
    {
      id: 2,
      name: 'Aminata Diop',
      country: 'Sénégal',
      category: 'professional',
      ranking: 1,
      competitions: 22,
      wins: 8,
      status: 'active'
    },
    {
      id: 3,
      name: 'Ramzi Boukhiam',
      country: 'Maroc',
      category: 'professional',
      ranking: 2,
      competitions: 45,
      wins: 18,
      status: 'active'
    }
  ]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    // Fetch dashboard data
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setStats({
          news: newsData.length,
          athletes: athletesData.length,
          federations: 3,
          partners: 2,
          galleries: 2,
          sliders: 3,
          users: 1,
          messages: 5
        });
        setActivity([
          {
            type: 'news',
            title: 'Africa Surf Tour 2025 : Les Dates Révélées',
            date: '2024-12-01T10:00:00Z',
            action: 'Nouvel article publié'
          },
          {
            type: 'athlete',
            title: 'Cherif Fall',
            date: '2024-11-15T14:30:00Z',
            action: 'Athlète ajouté'
          },
          {
            type: 'gallery',
            title: 'Africa Surf Tour 2024 - Sénégal',
            date: '2024-11-10T16:00:00Z',
            action: 'Galerie mise à jour'
          }
        ]);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin3256');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const menuItems = [
    { icon: LayoutDashboard, name: 'Tableau de Bord', id: 'dashboard' },
    { icon: Newspaper, name: 'Actualités', id: 'news' },
    { icon: Users, name: 'Athlètes', id: 'athletes' },
    { icon: Flag, name: 'Fédérations', id: 'federations' },
    { icon: Handshake, name: 'Partenaires', id: 'partners' },
    { icon: Image, name: 'Galerie', id: 'gallery' },
    { icon: Sliders, name: 'Sliders', id: 'sliders' },
    { icon: MessageSquare, name: 'Messages', id: 'messages' },
   { icon: ShipWheel, name: 'Tour', id: 'tour' },
    { icon: Settings, name: 'Paramètres', id: 'settings' }
  ];

  const handleDelete = (id: number, type: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      if (type === 'news') {
        setNewsData(newsData.filter(item => item.id !== id));
      } else if (type === 'athletes') {
        setAthletesData(athletesData.filter(item => item.id !== id));
      }
    }
  };

  const handleBulkDelete = (type: string) => {
    if (selectedItems.length === 0) return;
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedItems.length} élément(s) ?`)) {
      if (type === 'news') {
        setNewsData(newsData.filter(item => !selectedItems.includes(item.id)));
      } else if (type === 'athletes') {
        setAthletesData(athletesData.filter(item => !selectedItems.includes(item.id)));
      }
      setSelectedItems([]);
    }
  };

  const toggleSelectItem = (id: number) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const selectAllItems = (data: any[]) => {
    const allIds = data.map(item => item.id);
    setSelectedItems(selectedItems.length === allIds.length ? [] : allIds);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tableau de Bord</h1>
        <p className="text-gray-600">Bienvenue dans l'administration ASCsurf</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-100 text-blue-600 p-3 rounded-lg">
              <Newspaper className="h-6 w-6" />
            </div>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.news}</div>
          <div className="text-sm text-gray-500">Actualités</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-orange-100 text-orange-600 p-3 rounded-lg">
              <Users className="h-6 w-6" />
            </div>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.athletes}</div>
          <div className="text-sm text-gray-500">Athlètes</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-green-100 text-green-600 p-3 rounded-lg">
              <Flag className="h-6 w-6" />
            </div>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.federations}</div>
          <div className="text-sm text-gray-500">Fédérations</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-100 text-purple-600 p-3 rounded-lg">
              <MessageSquare className="h-6 w-6" />
            </div>
            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">{stats.messages}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats.messages}</div>
          <div className="text-sm text-gray-500">Messages</div>
        </div>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Actions Rapides</h2>
          <div className="space-y-4">
            <button 
              onClick={() => setActiveSection('news')}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="font-medium">Nouvel Article</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            <button 
              onClick={() => setActiveSection('athletes')}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-orange-100 text-orange-600 p-2 rounded-lg">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="font-medium">Nouvel Athlète</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            <button 
              onClick={() => setActiveSection('gallery')}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 text-green-600 p-2 rounded-lg">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="font-medium">Nouvelle Galerie</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
            
            <button 
              onClick={() => setActiveSection('sliders')}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors w-full"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 text-purple-600 p-2 rounded-lg">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="font-medium">Nouveau Slider</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Activité Récente</h2>
          <div className="space-y-6">
            {activity.length > 0 ? (
              activity.map((item: any, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${
                    item.type === 'news' ? 'bg-blue-100 text-blue-600' : 
                    item.type === 'athlete' ? 'bg-orange-100 text-orange-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    {item.type === 'news' ? <Newspaper className="h-5 w-5" /> : 
                     item.type === 'athlete' ? <Users className="h-5 w-5" /> :
                     <Image className="h-5 w-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                      <span className="text-sm text-gray-500">{formatDate(item.date)}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{item.action}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">Aucune activité récente</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDataTable = (data: any[], type: string, columns: string[]) => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Table Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestion des {type === 'news' ? 'Actualités' : 'Athlètes'}
          </h2>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Ajouter</span>
          </button>
        </div>
        
        {/* Search and Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>Filtrer</span>
          </button>
          {selectedItems.length > 0 && (
            <button 
              onClick={() => handleBulkDelete(type)}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedItems.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedItems.length === data.length}
                  onChange={() => selectAllItems(data)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {columns.map((column, index) => (
                <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column}
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.filter(item => 
              searchTerm === '' || 
              Object.values(item).some(value => 
                value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
              )
            ).map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => toggleSelectItem(item.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                {type === 'news' ? (
                  <>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.title}</div>
                          <div className="text-sm text-gray-500">{item.author}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.status === 'published' ? 'Publié' : 'Brouillon'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.views} vues
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{item.country}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">#{item.ranking}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{item.competitions}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{item.wins}</td>
                  </>
                )}
                <td className="px-6 py-4 text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button className="text-blue-600 hover:text-blue-900">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-indigo-600 hover:text-indigo-900">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(item.id, type)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'news':
        return <AdminNews />;
      case 'athletes':
        return <AdminAthletes />;
      case 'federations':
        return <AdminFederations />;
      case 'partners':
        return <AdminPartners />;
      case 'gallery':
        return <AdminGallery />;
      case 'sliders':
        return <AdminSliders />;
      case 'messages':
        return <AdminMessages />;
       case 'tour':
        return <AdminTour />;
      case 'settings':
        return <AdminSettings />;
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <span className="font-bold text-lg">ASCsurf Admin</span>
        </div>
        <button 
          onClick={handleLogout}
          className="text-red-600 hover:text-red-800 transition-colors"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 transition-transform duration-300 ease-in-out`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 text-white p-2 rounded-lg">
                <LayoutDashboard className="h-6 w-6" />
              </div>
              <div>
                <div className="font-bold text-lg">ASCsurf</div>
                <div className="text-xs text-gray-500">Administration</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <button 
                    onClick={() => {
                      setActiveSection(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg w-full text-left ${
                      activeSection === item.id 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-700 hover:bg-gray-100'
                    } transition-colors`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <button 
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 w-full rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;